import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogFooter, DialogClose, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Camera } from "lucide-react";

interface ProductInput {
  id?: number; // for editing
  name: string;
  description?: string;
  price: number;
  category?: string;
  image?: string | null;
}

export default function AddProductDialog({ open, onOpenChange, onAdd }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (prod: ProductInput) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [imgPreview, setImgPreview] = useState<string|null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() {
    setName(""); setDescription(""); setPrice(""); setCategory(""); setImgPreview(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !price) return;
    onAdd({ name: name.trim(), description: description.trim(), price: Number(price), category: category.trim(), image: imgPreview });
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[78vh] overflow-y-auto pb-36 md:pb-8">
        <DialogTitle className="sr-only">Add Product/Service</DialogTitle>
        <DialogDescription>Define a new product/service for faster transactions and easy analytics.</DialogDescription>
        <form className="flex flex-col gap-4 mt-3" onSubmit={handleSubmit}>
          <Input className="w-full" placeholder="Name *" value={name} onChange={e => setName(e.target.value)} required />
          <Input className="w-full" placeholder="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} />
          <Input className="w-full" type="number" min="0" step="0.01" placeholder="Price *" value={price} onChange={e => setPrice(e.target.value)} required />
          <Input className="w-full" placeholder="Category/Tag (optional)" value={category} onChange={e => setCategory(e.target.value)} />
          <div className="flex items-center gap-3">
            <label className="cursor-pointer flex items-center gap-2 text-sm text-muted-foreground">
              <Camera size={20} />
              <span>Upload image</span>
              <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={e => {
                const file = e.target.files?.[0];
                if (file) setImgPreview(URL.createObjectURL(file));
              }} />
            </label>
            {imgPreview && <img alt="preview" src={imgPreview} className="h-12 w-12 rounded shadow ml-2 object-cover" />}
            {imgPreview && <button type="button" className="ml-2 text-xs text-destructive underline" onClick={()=>setImgPreview(null)}>Remove</button>}
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-primary">Save</Button>
            <DialogClose asChild><Button type="button" variant="ghost">Cancel</Button></DialogClose>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}